// src/utils/ocr/locationFacility.ts
import type { BBoxEntry, BBoxResource } from '../../../types/bbox';

/**
 * Discern does not put a facility on the resources that need one. It extracts
 * the facility once per group as its own top-level `Location` resource, and
 * every resource in that group shares it — one `results.json` per sub-job means
 * one Location per group, sitting in the same BBOX array as the Surgery / Lab /
 * Encounter that should display it.
 *
 * These helpers lift that Location's fields onto the resource being curated, by
 * pushing them into the record's `_bboxMap` under namespaced synthetic labels.
 * Which forms actually consume them is decided by `ocrMap` in
 * RESOURCE_CURATION_CONFIG, exactly like every real OCR label — so a form type
 * that has no facility block needs no exclusion here, it simply doesn't list
 * these labels.
 *
 * The label list mirrors `location_finder`'s output in discern-modelling
 * (`AutoCuration/sagemaker/code/resouces_detection.py`, the `out` dict). Keep
 * them in step: an unmapped label is dropped silently, with no error.
 */
const LOCATION_LABEL_TO_OCR_LABEL: Record<string, string> = {
  name: 'locationName',
  city: 'locationCity',
  region: 'locationRegion',
  postalCode: 'locationPostalCode',
  country: 'locationCountry',
};

/**
 * Every synthetic label `applyLocationFacility` can produce. An ocrMap entry
 * naming anything else is dropped in silence, so the config test asserts against
 * this list rather than trusting the spelling.
 */
export const LOCATION_OCR_LABELS: readonly string[] = Object.values(
  LOCATION_LABEL_TO_OCR_LABEL
);

const LOCATION_RESOURCE_TITLE = 'location';

/**
 * `details.bounding_boxes` reaches the client through a GraphQLJSON passthrough
 * scalar, so `BBoxResource` describes the model's contract rather than anything
 * the type system enforced. Read the payload through these views instead, so a
 * resource with no title or a field with no label degrades rather than throws.
 */
type UntrustedField = { label?: string | null; text?: string | null } | null;
type UntrustedResource = { Resource_title?: string | null } | null;

/**
 * `Location` is metadata, so it never maps to a form (see
 * mapBBoxTitleToFormType). Matched by substring, not equality, to catch the
 * same title variants that function already tolerates (`Location`,
 * `Locations`, `Location Details`, titles with surrounding text).
 */
export function findLocationResource(
  resources: BBoxResource[] | null | undefined
): BBoxResource | null {
  if (!Array.isArray(resources)) return null;

  const found = (resources as UntrustedResource[]).find((resource) =>
    resource?.Resource_title?.toLowerCase().includes(LOCATION_RESOURCE_TITLE)
  );

  return (found as BBoxResource | undefined) ?? null;
}

function trimmedText(entry: UntrustedField | undefined): string {
  return typeof entry?.text === 'string' ? entry.text.trim() : '';
}

/**
 * Add the group's facility fields to `bboxMap` under their synthetic labels.
 * Mutates `bboxMap` (which is freshly built per record) and never touches the
 * Location resource, which is shared React state.
 *
 * Each field is judged only by its own text: a blank/whitespace-only `name`
 * (or any other single field) must not withhold the rest of the address
 * block — a document can carry a clean city/region/country without ever
 * stating the facility's name.
 */
export function applyLocationFacility(
  bboxMap: Record<string, BBoxEntry[]>,
  location: BBoxResource | null | undefined
): void {
  if (!location || !Array.isArray(location.Resource_data)) return;

  const byLabel = new Map<string, UntrustedField>();
  for (const field of location.Resource_data as UntrustedField[]) {
    if (field?.label && !byLabel.has(field.label)) {
      byLabel.set(field.label, field);
    }
  }

  for (const [locationLabel, ocrLabel] of Object.entries(
    LOCATION_LABEL_TO_OCR_LABEL
  )) {
    const field = byLabel.get(locationLabel);
    const text = trimmedText(field);
    if (!field || !text) continue;

    bboxMap[ocrLabel] = [{ ...(field as BBoxEntry), label: ocrLabel, text }];
  }
}
